import { Kind } from '@core/models/installation.model';
import { CHART_SERIES, CHART_SERIES_NEUTRAL } from './tokens.generated';

export type RoleCard =
  | 'grid'
  | 'energy'
  | 'power'
  | 'voltage'
  | 'current'
  | 'state_of_charge'
  | 'heatpump'
  | 'flow';

export type RoleAxis =
  | 'energy'
  | 'power'
  | 'voltage'
  | 'current'
  | 'temperature'
  | 'percent'
  | 'flow'
  | 'ratio';

export interface UnitConversion {
  displayUnit: string;
  factor: number;
  decimals: number;
}

// Every unit conversion on the installation pages. The chart, the tooltip, the data explorer and
// its CSV all read this table, so the screen and the export cannot disagree.
export const UNIT_CONVERSIONS: Record<string, UnitConversion> = {
  Wh: { displayUnit: 'kWh', factor: 1 / 1000, decimals: 2 },
  W: { displayUnit: 'kW', factor: 1 / 1000, decimals: 2 },
  m3ps: { displayUnit: 'l/min', factor: 60000, decimals: 2 },
  V: { displayUnit: 'V', factor: 1, decimals: 1 },
  A: { displayUnit: 'A', factor: 1, decimals: 2 },
  degC: { displayUnit: '°C', factor: 1, decimals: 1 },
  '%': { displayUnit: '%', factor: 1, decimals: 0 },
  ratio: { displayUnit: '', factor: 1, decimals: 2 },
};

export function conversionFor(unit: string): UnitConversion {
  return UNIT_CONVERSIONS[unit] ?? { displayUnit: unit, factor: 1, decimals: 2 };
}

export interface RoleLayout {
  card: RoleCard;
  axis: RoleAxis;
  // Presentation only: a `down` series is drawn below the axis. The value itself is already
  // signed by its binding and is never changed.
  direction: 'up' | 'down';
  // Index into CHART_SERIES; null draws the neutral token.
  colorToken: number | null;
  sortOrder: number;
  // The balance of the card's other series, drawn as a dashed line over their bars.
  balance?: boolean;
}

export interface ResolvedRoleLayout extends RoleLayout {
  // A role with no catalogue entry gets its own card, keyed by the role code.
  cardKey: string;
  chart: 'bar' | 'line';
  known: boolean;
}

const up = 'up' as const;
const down = 'down' as const;

// Keyed by role code. Card order on the page follows the lowest sortOrder a card holds, and
// series order within a card follows sortOrder; the order of the API response is not used.
export const ROLE_LAYOUT: Readonly<Record<string, RoleLayout>> = {
  grid_import: { card: 'grid', axis: 'energy', direction: up, colorToken: 0, sortOrder: 10 },
  grid_export: { card: 'grid', axis: 'energy', direction: down, colorToken: 1, sortOrder: 20 },
  net_grid_energy: {
    card: 'grid',
    axis: 'energy',
    direction: up,
    colorToken: 11,
    sortOrder: 990,
    balance: true,
  },

  pv_production: { card: 'energy', axis: 'energy', direction: up, colorToken: 2, sortOrder: 30 },
  battery_charge: { card: 'energy', axis: 'energy', direction: up, colorToken: 3, sortOrder: 40 },
  battery_discharge: {
    card: 'energy',
    axis: 'energy',
    direction: down,
    colorToken: 4,
    sortOrder: 50,
  },
  ev_charging: { card: 'energy', axis: 'energy', direction: up, colorToken: 5, sortOrder: 60 },
  heatpump_consumption: {
    card: 'energy',
    axis: 'energy',
    direction: up,
    colorToken: 6,
    sortOrder: 70,
  },
  submeter_consumption: {
    card: 'energy',
    axis: 'energy',
    direction: up,
    colorToken: 11,
    sortOrder: 80,
  },
  submeter_feed_in: {
    card: 'energy',
    axis: 'energy',
    direction: down,
    colorToken: 11,
    sortOrder: 110,
  },
  household_consumption: {
    card: 'energy',
    axis: 'energy',
    direction: up,
    colorToken: null,
    sortOrder: 991,
  },

  active_power_import: {
    card: 'power',
    axis: 'power',
    direction: up,
    colorToken: 0,
    sortOrder: 120,
  },
  active_power_export: {
    card: 'power',
    axis: 'power',
    direction: down,
    colorToken: 1,
    sortOrder: 130,
  },
  active_power_l1: { card: 'power', axis: 'power', direction: up, colorToken: 7, sortOrder: 140 },
  active_power_l2: { card: 'power', axis: 'power', direction: up, colorToken: 8, sortOrder: 150 },
  active_power_l3: { card: 'power', axis: 'power', direction: up, colorToken: 9, sortOrder: 160 },
  battery_power: { card: 'power', axis: 'power', direction: up, colorToken: 3, sortOrder: 240 },

  voltage_l1: { card: 'voltage', axis: 'voltage', direction: up, colorToken: 7, sortOrder: 170 },
  voltage_l2: { card: 'voltage', axis: 'voltage', direction: up, colorToken: 8, sortOrder: 180 },
  voltage_l3: { card: 'voltage', axis: 'voltage', direction: up, colorToken: 9, sortOrder: 190 },

  current_l1: { card: 'current', axis: 'current', direction: up, colorToken: 7, sortOrder: 200 },
  current_l2: { card: 'current', axis: 'current', direction: up, colorToken: 8, sortOrder: 210 },
  current_l3: { card: 'current', axis: 'current', direction: up, colorToken: 9, sortOrder: 220 },

  battery_state_of_charge: {
    card: 'state_of_charge',
    axis: 'percent',
    direction: up,
    colorToken: 10,
    sortOrder: 230,
  },

  heatpump_room_temperature: {
    card: 'heatpump',
    axis: 'temperature',
    direction: up,
    colorToken: 6,
    sortOrder: 250,
  },
  heatpump_outside_air_temperature: {
    card: 'heatpump',
    axis: 'temperature',
    direction: up,
    colorToken: 3,
    sortOrder: 260,
  },
  heatpump_return_water_temperature: {
    card: 'heatpump',
    axis: 'temperature',
    direction: up,
    colorToken: 2,
    sortOrder: 270,
  },
  heatpump_leaving_water_temperature: {
    card: 'heatpump',
    axis: 'temperature',
    direction: up,
    colorToken: 4,
    sortOrder: 280,
  },
  heatpump_cooling_target_temperature: {
    card: 'heatpump',
    axis: 'temperature',
    direction: up,
    colorToken: 10,
    sortOrder: 290,
  },
  heatpump_heating_target_temperature: {
    card: 'heatpump',
    axis: 'temperature',
    direction: up,
    colorToken: 5,
    sortOrder: 300,
  },

  heatpump_flow_rate: { card: 'flow', axis: 'flow', direction: up, colorToken: 3, sortOrder: 310 },
  heatpump_heat_transfer_rate: {
    card: 'flow',
    axis: 'power',
    direction: up,
    colorToken: 6,
    sortOrder: 320,
  },
};

// No catalogue role carries a heat pump's thermal energy or COP, so the installation pages read them
// from the heat pump's device energyflow and lay them out under these codes. They are kept apart
// from ROLE_LAYOUT because no point in the database has them.
export const ENERGYFLOW_LAYOUT: Readonly<Record<string, RoleLayout>> = {
  heatpump_thermal_energy: {
    card: 'energy',
    axis: 'energy',
    direction: up,
    colorToken: 7,
    sortOrder: 72,
  },
  heatpump_coefficient_of_performance: {
    card: 'energy',
    axis: 'ratio',
    direction: up,
    colorToken: 8,
    sortOrder: 74,
  },
};

// Roles the installation pages leave out of the cards and the data explorer. Household consumption
// is derived from the grid and PV points and reads as a large negative number when the PV point has
// no data, so it stays off the page until the derivation handles a missing input.
export const HIDDEN_ROLES: ReadonlySet<string> = new Set<string>(['household_consumption']);

// Cards whose roles can occur once per heat pump: one card per heat pump when there are several.
export const PER_SOURCE_CARDS: ReadonlySet<RoleCard> = new Set<RoleCard>(['heatpump', 'flow']);

const FALLBACK_SORT_ORDER = 10000;

export function chartFor(kind: Kind): 'bar' | 'line' {
  return kind === 'average' ? 'line' : 'bar';
}

export function layoutFor(roleCode: string, kind: Kind): ResolvedRoleLayout {
  const layout = ROLE_LAYOUT[roleCode] ?? ENERGYFLOW_LAYOUT[roleCode];
  if (layout) {
    return { ...layout, cardKey: layout.card, chart: chartFor(kind), known: true };
  }
  return {
    card: 'energy',
    cardKey: `role:${roleCode}`,
    axis: 'energy',
    direction: 'up',
    colorToken: null,
    sortOrder: FALLBACK_SORT_ORDER,
    chart: chartFor(kind),
    known: false,
  };
}

// A second point of the same role on one card takes the next colour, so two PV arrays do not
// share a hue; the source strip is what names them.
export function seriesColor(colorToken: number | null, occurrence = 0): string {
  if (colorToken === null) {
    return CHART_SERIES_NEUTRAL;
  }
  return CHART_SERIES[(colorToken + occurrence) % CHART_SERIES.length];
}
